import { useContext } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { useLocation } from '@reach/router';
import { ResourceSetContext } from '../context/ResourceSetContext';
import { mapperWithFunction } from '../common/utils';
import { generateSchema } from '../utils/schemaUtils';

const STRUCTURED_DATA_MAP = {
  title: ({ menuLabel, headline }) => {
    return headline || menuLabel;
  },
  menuLabel: ({ menuLabel, headline }) => {
    return menuLabel || headline;
  },
  description: ['description', 'description'],
  image: ['image', 'fluid', 'src'],
  altText: ['altText'],
  meta: ({ meta }) => {
    return meta ? JSON.parse(meta.internal.content) : {};
  },
  noIndex: ['noIndex'],
  noFollow: ['noFollow'],
};

const useSeo = (data, FALLBACK_DATA_MAP, template) => {
  const context = useContext(ResourceSetContext);
  const globalRS = context.getResourcesForComponent('global');
  const { pathname, search } = useLocation(); 
  const { site } = useStaticQuery(query);

  let structuredData = {};
  const fallbackData = mapperWithFunction(data, FALLBACK_DATA_MAP);

  if (data?.structuredData) {
    structuredData = mapperWithFunction(
      data.structuredData,
      STRUCTURED_DATA_MAP,
    );
  }

  const image = structuredData.image || fallbackData.image;
  
  // Build complete URL including query parameters for pagination
  let url;
  if (typeof window !== 'undefined') {
    // Client-side: use window.location for accurate query params
    url = `${site.siteMetadata.siteUrl}${window.location.pathname}${window.location.search}`;
  } else {
    // Server-side: use Reach Router location
    const queryString = search || '';
    url = `${site.siteMetadata.siteUrl}${pathname}${queryString}`;
  }
  
  // Only add trailing slash if no query parameters exist
  if (!url.includes('?') && !url.endsWith('/')) {
    url += '/';
  }

  let title =
    structuredData.title || fallbackData.title || fallbackData.headline;
  if (title && !title.endsWith('Singita'))
    title += ` | ${globalRS.websiteName}`;

  const seoData = {
    ...fallbackData,
    ...structuredData,
    url: url,
    origin: site.siteMetadata.siteUrl || globalRS.websiteURL,
    sdTitle: structuredData.title,
    title: title,
    menuLabel:
      structuredData.menuLabel || fallbackData.title || fallbackData.headline,
    description:
      structuredData.description ||
      fallbackData.description,
    image: image ? `${image.replace('./img', '/img')}` : globalRS.image,
    altText: structuredData.altText || fallbackData.altText || globalRS.altText,
    breadcrumb: fallbackData.breadcrumb || pathname.split('/')[1],
    websiteName: globalRS.websiteName,
    logo: globalRS.logo,
    telephone: globalRS.telephone,
    email: globalRS.email,
    streetAddress: globalRS.streetAddress,
    addressLocality: globalRS.addressLocality,
    addressRegion: globalRS.addressRegion,
    postalCode: globalRS.postalCode,
    addressCountry: globalRS.addressCountry,
  };

  // Generate schema markup if template is provided
  const schemaJsonLd = template ? generateSchema(seoData, template) : null;

  return {
    ...seoData,
    schemaJsonLd,
  };
};

export default useSeo;

const query = graphql`
  query SEO {
    site {
      siteMetadata {
        siteUrl
      }
    }
  }
`;